import { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";

// styles
import Styles from "./createBeneficiaryModal.module.css";

// redux
import { useSelector, useDispatch } from "react-redux";
import { _createBeneficiary } from "../../../profile/redux/actions";
import { _getCurrencies } from "../../deposit-withdraw/redux/actions";
import { addBeneficiary } from "../../../../redux/slices/beneficiariesSlice";

// components
import Input from "../../../../components/Input/Input";
import Button from "../../../../components/Button/Button";
import WenbitModal from "../../../../components/Modal/Modal";
import SelectCoinInput from "../../../../components/Input/SelectCoinInput/SelectCoinInput";
import FileInput from "../../../../components/Input/FileInput/FileInput";
// No Select import needed, using Input with type="select"

// utils
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from "../../../snackbar/helpers";
import { hideCreateBeneficiaryModal } from "./helpers";

// form validation
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { API } from "../../../../utility/API";

function CreateBeneficiaryModal() {
  const dispatch = useDispatch()
  const modalOpen = useSelector(
    (state) => state.createBeneficiaryModalState.open
  )
  const modalData = useSelector(
    (state) => state.createBeneficiaryModalState.data
  )
  const currencies = useSelector((state) => state.currencies.value)

  const [selectedBeneficiaryCoinID, setSelectedBeneficiaryCoinID] = useState(
    modalData.selectedCoin || null // Don't set default until modal opens
  )
  const [loading, setLoading] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [isFiatCurrency, setIsFiatCurrency] = useState(false)
  const [formKey, setFormKey] = useState(0) // Used to force re-render the form
  const [disclaimerChecked, setDisclaimerChecked] = useState(false)

  // Step management
  const [currentStep, setCurrentStep] = useState(1)

  // Payment method states
  const [paymentMethods, setPaymentMethods] = useState([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
  const [paymentMethodDetails, setPaymentMethodDetails] = useState(null)
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false)
  const [paymentMethodDetailsLoading, setPaymentMethodDetailsLoading] = useState(false)

  // Network state handling (for crypto)
  const [networkOptions, setNetworkOptions] = useState([])
  const [selectedNetwork, setSelectedNetwork] = useState("")
  const [networkLoading, setNetworkLoading] = useState(false)

  // File upload state handling
  const [uploadedFiles, setUploadedFiles] = useState({})

  // Step 1 validation schema (basic info + payment method)
  const step1Schema = Yup.object().shape({
    name: Yup.string()
      .required("Beneficiary name is required")
      .min(2, "Beneficiary name must be at least 2 characters")
      .max(64, "Beneficiary name must be 64 characters or less"),
    description: Yup.string().max(255),
    payment_method: Yup.string().required("Payment method selection is required")
  })

  // Dynamic validation schema for step 2 based on payment method details
  const createStep2Schema = () => {
    const schemaFields = {
      // Always include step 1 fields in step 2 validation
      name: Yup.string()
        .required("Beneficiary name is required")
        .min(2, "Beneficiary name must be at least 2 characters")
        .max(64, "Beneficiary name must be 64 characters or less"),
      description: Yup.string().max(255),
      payment_method: Yup.string().required("Payment method selection is required")
    }

    // For crypto currencies, add address and network validation
    if (!isFiatCurrency) {
      schemaFields.address = Yup.string()
        .required("Beneficiary address is required")
        .min(10, "Address must be at least 10 characters")

      schemaFields.network = Yup.string()
        .required("Network is required")
    }

    // Add dynamic fields from payment method details
    if (paymentMethodDetails?.payment_method_details) {
      paymentMethodDetails.payment_method_details.forEach(field => {
        const fieldName = field.field_name
        // Create a safe field name for form validation by removing special characters
        const safeFieldName = fieldName.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()

        // Skip address and network for crypto as they're handled above
        if (!isFiatCurrency && ['address', 'network'].includes(fieldName.toLowerCase())) {
          return
        }

        // Handle file upload fields differently
        if (field.upload) {
          // For file uploads, we validate the uploaded files array
          if (field.required) {
            schemaFields[safeFieldName] = Yup.mixed()
              .test('fileRequired', `${fieldName} file is required`, function () {
                const files = uploadedFiles[safeFieldName]
                return files && files.length > 0
              })
          }
          return
        }

        // Create validation rules based on field name
        switch (fieldName.toLowerCase()) {
          case 'address':
            schemaFields[safeFieldName] = Yup.string()
              .required(`${fieldName} is required`)
              .min(10, "Address must be at least 10 characters")
            break
          case 'network':
            schemaFields[safeFieldName] = Yup.string()
              .required(`${fieldName} is required`)
            break
          case 'relation':
            schemaFields[safeFieldName] = Yup.string()
              .required(`${fieldName} is required`)
            break
          case 'bank_name':
            schemaFields[safeFieldName] = Yup.string()
              .required(`${fieldName} is required`)
              .min(2, "Bank name must be at least 2 characters")
              .max(100, "Bank name must be 100 characters or less")
            break
          case 'bank_code':
          case 'iban':
          case 'account_number':
            schemaFields[safeFieldName] = Yup.string()
              .required(`${fieldName} is required`)
            break
          case 'sort_code':
            schemaFields[safeFieldName] = Yup.string()
              .required(`${fieldName} is required`)
              .matches(/^\d{2}-\d{2}-\d{2}$/, "Sort code must be in format XX-XX-XX")
            break
          case 'swift_bic':
            schemaFields[safeFieldName] = Yup.string()
              .required(`${fieldName} is required`)
              .matches(/^[A-Z0-9]{8,11}$/, "Invalid SWIFT/BIC format")
            break
          case 'bank_address':
            schemaFields[safeFieldName] = Yup.string()
              .required(`${fieldName} is required`)
              .min(5, "Bank address must be at least 5 characters")
            break
          case 'bank_country':
            schemaFields[safeFieldName] = Yup.string()
              .required(`${fieldName} is required`)
            break
          case 'acc_holder_full_name':
            schemaFields[safeFieldName] = Yup.string()
              .required(`${fieldName} is required`)
              .min(2, "Account holder name must be at least 2 characters")
              .max(100, "Account holder name must be 100 characters or less")
            break
          default:
            if (field.required) {
              schemaFields[safeFieldName] = Yup.string()
                .required(`${fieldName} is required`)
            } else {
              schemaFields[safeFieldName] = Yup.string()
            }
        }
      })
    }

    return Yup.object().shape(schemaFields)
  }

  // Initialize form with appropriate schema based on current step
  const getCurrentSchema = () => {
    if (currentStep === 1) {
      return step1Schema
    } else {
      return createStep2Schema()
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting },
    reset,
    trigger,
    getValues,
    setValue,
    clearErrors,
    watch
  } = useForm({
    resolver: yupResolver(getCurrentSchema()),
    mode: "onChange",
    reValidateMode: "onChange"
  })

  // Function to fetch payment methods for a currency
  const fetchPaymentMethods = async (currencyId) => {
    if (!currencyId) return

    setPaymentMethodsLoading(true)
    try {
      const response = await API.get(`/trading/account/beneficiaries/payment_methods?currency=${currencyId}`)
      console.log(response)
      // Response is directly an array, not wrapped in data property
      if (response) {
        // Transform the response to match expected structure
        const transformedPaymentMethods = response.data.map(item => ({
          id: item.payment_method_id,
          name: item.payment_method.name,
          icon_url: item.payment_method.icon_url,
          description: item.payment_method.description,
          payment_type: item.payment_method.payment_type,
          fee_percentage: item.payment_method.fee_percentage,
          position: item.payment_method.position
        }))
        console.log(transformedPaymentMethods)
        setPaymentMethods(transformedPaymentMethods)

        // Auto-select first payment method if only one is available
        if (transformedPaymentMethods.length === 1) {
          const firstPaymentMethod = transformedPaymentMethods[0]
          setSelectedPaymentMethod(firstPaymentMethod)
          setValue("payment_method", firstPaymentMethod.id.toString())

          // Call handlePaymentMethodChange to fetch details and trigger logging
          handlePaymentMethodChange(null, firstPaymentMethod.id.toString())
        }
      } else {
        setPaymentMethods([])
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error)
      if (modalOpen) {
        showErrorSnackbar({
          alertMessage: "Failed to fetch payment methods for this currency"
        })
      }
      setPaymentMethods([])
    } finally {
      setPaymentMethodsLoading(false)
    }
  }

  // Function to fetch payment method details
  const fetchPaymentMethodDetails = async (paymentMethodId) => {
    if (!paymentMethodId) return

    setPaymentMethodDetailsLoading(true)
    try {
      const response = await API.get(`/trading/account/beneficiaries/payment_method?payment_method_id=${paymentMethodId}&currency=${selectedBeneficiaryCoinID}`)

      if (response.data) {
        setPaymentMethodDetails(response.data)

        // If it's a crypto payment method, also fetch networks if not already fetched
        if (!isFiatCurrency && selectedBeneficiaryCoinID && networkOptions.length === 0) {
          await fetchNetworks(selectedBeneficiaryCoinID)
        }
      } else {
        setPaymentMethodDetails(null)
      }
    } catch (error) {
      console.error("Error fetching payment method details:", error)
      if (modalOpen) {
        showErrorSnackbar({
          alertMessage: "Failed to fetch payment method details"
        })
      }
      setPaymentMethodDetails(null)
    } finally {
      setPaymentMethodDetailsLoading(false)
    }
  }

  // Function to fetch networks for a given currency (for crypto)
  const fetchNetworks = async (currencyId) => {
    if (!currencyId) return

    setNetworkLoading(true)
    try {
      const response = await API.get(`/trading/account/beneficiaries/networks/${currencyId}`)

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const options = response.data.map((network) => ({
          value: network,
          label: network
        }))

        const networkSelectOptions = [
          { value: "", label: "Select Network" },
          ...options
        ]

        setNetworkOptions(networkSelectOptions)

        // Select the first network by default
        if (options.length > 0) {
          setSelectedNetwork(options[0].value)
          setValue("network", options[0].value)
        }
      } else {
        setNetworkOptions([])
        setSelectedNetwork("")
        setValue("network", "")
      }
    } catch (error) {
      console.error("Error fetching networks:", error)
      if (modalOpen) {
        showErrorSnackbar({
          alertMessage: "Failed to fetch networks for this currency"
        })
      }
      setNetworkOptions([])
      setSelectedNetwork("")
      setValue("network", "")
    } finally {
      setNetworkLoading(false)
    }
  }

  // Update currency type and fetch payment methods when selection changes
  useEffect(() => {
    if (!selectedBeneficiaryCoinID) return

    const selectedCurrency = currencies.find(c => c.id === selectedBeneficiaryCoinID)
    const isFiat = selectedCurrency?.type === 'fiat'

    // Update currency type if it has changed
    if (isFiat !== isFiatCurrency) {
      setIsFiatCurrency(isFiat)
      // Reset payment method selection when currency type changes
      setSelectedPaymentMethod(null)
      setPaymentMethodDetails(null)
      setValue("payment_method", "")

      // Reset form with updated schema
      if (currentStep === 1) {
        reset({}, {
          resolver: yupResolver(step1Schema),
          keepDefaultValues: false
        })
      }
    }

    // Always fetch payment methods for the selected currency
    fetchPaymentMethods(selectedBeneficiaryCoinID)

    // For crypto currencies, also fetch networks
    if (!isFiat) {
      fetchNetworks(selectedBeneficiaryCoinID)
    } else {
      // Clear networks for fiat currencies
      setNetworkOptions([])
      setSelectedNetwork("")
      setValue("network", "")
    }
  }, [selectedBeneficiaryCoinID, currencies.length])

  // Update form resolver when step changes or payment method details change
  useEffect(() => {
    const newResolver = yupResolver(getCurrentSchema())
    reset(getValues(), {
      resolver: newResolver,
      keepValues: true,
      keepErrors: false
    })
  }, [currentStep, paymentMethodDetails])

  // Handle payment method selection
  const handlePaymentMethodChange = (e, value) => {
    const paymentMethodId = value
    const paymentMethod = paymentMethods.find(pm => pm.id.toString() === paymentMethodId)

    setSelectedPaymentMethod(paymentMethod)
    setValue("payment_method", paymentMethodId)
    clearErrors("payment_method")
    console.log("=================================================")
    console.log(paymentMethodId)
    console.log(paymentMethod)
    console.log("=================================================")

    if (paymentMethodId) {
      fetchPaymentMethodDetails(paymentMethodId)
    } else {
      setPaymentMethodDetails(null)
    }
  }

  // Validate fields for current step
  const validateFields = async () => {
    const result = await trigger()
    return result
  }

  // Handle step 1 submission (proceed to step 2)
  const handleStep1Submit = async (formData) => {
    const isFormValid = await validateFields()
    if (!isFormValid) {
      showErrorSnackbar({
        alertMessage: "Please fill in all required fields correctly"
      })
      return
    }

    if (!selectedPaymentMethod) {
      showErrorSnackbar({
        alertMessage: "Please select a payment method"
      })
      return
    }

    // Store step 1 data
    const step1Data = {
      name: formData.name,
      description: formData.description,
      payment_method: selectedPaymentMethod.id.toString()
    }

    // Move to step 2 and preserve step 1 data
    setCurrentStep(2)

    // Reset form with step 2 schema but preserve step 1 data
    setTimeout(() => {
      reset(step1Data, {
        resolver: yupResolver(createStep2Schema()),
        keepValues: false
      })
      setFormKey(prev => prev + 1)
    }, 50)
  }

  // Handle final submission (step 2)
  const onFinalSubmit = async (formData) => {
    const isFormValid = await validateFields()
    if (!isFormValid) {
      showErrorSnackbar({
        alertMessage: "Please fill in all required fields correctly"
      })
      return
    }

    // Check required file uploads
    if (paymentMethodDetails?.payment_method_details) {
      const missingFiles = []
      paymentMethodDetails.payment_method_details.forEach(field => {
        if (field.upload && field.required) {
          const safeFieldName = field.field_name.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()
          const files = uploadedFiles[safeFieldName]
          if (!files || files.length === 0) {
            const fieldLabel = field.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            missingFiles.push(fieldLabel)
          }
        }
      })

      if (missingFiles.length > 0) {
        showErrorSnackbar({
          alertMessage: `Please upload required files: ${missingFiles.join(', ')}`
        })
        return
      }
    }

    // Check if disclaimer is checked
    if (!disclaimerChecked) {
      showErrorSnackbar({
        alertMessage: "Please acknowledge the disclaimer"
      })
      return
    }

    setLoading(true)
    setButtonLoading(true)

    // Get all form data including hidden fields
    const allFormData = getValues()

    // Helper function to convert file to base64
    const fileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    }

    try {
      // Create JSON payload
      const jsonPayload = {
        currency: selectedBeneficiaryCoinID,
        name: allFormData.name || formData.name,
        description: allFormData.description || formData.description || "",
        payment_method_id: selectedPaymentMethod.id
      }

      // Add network for crypto currencies
      if (!isFiatCurrency && (allFormData.network || formData.network)) {
        jsonPayload.network = allFormData.network || formData.network
      }

      // Initialize data object for additional fields
      jsonPayload.data = {}

      // Add fields from payment method details
      if (paymentMethodDetails?.payment_method_details) {
        for (const field of paymentMethodDetails.payment_method_details) {
          const fieldName = field.field_name
          // Create a safe field name for form data retrieval
          const safeFieldName = fieldName.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()

          // Skip network as it's handled above as a separate field
          if (fieldName.toLowerCase() === 'network') {
            continue
          }

          // Handle file upload fields - convert files to base64
          if (field.upload) {
            const files = uploadedFiles[safeFieldName]
            if (files && files.length > 0) {
              // Convert files to base64 strings
              const base64Files = await Promise.all(
                files.map(file => fileToBase64(file))
              )
              // Store as array of base64 strings
              jsonPayload.data[fieldName] = base64Files
            }
            continue
          }

          // Handle non-file fields
          if (allFormData[safeFieldName] || formData[safeFieldName]) {
            jsonPayload.data[fieldName] = allFormData[safeFieldName] || formData[safeFieldName]
          }
        }
      }

      // For crypto currencies, include address
      if (!isFiatCurrency && (allFormData.address || formData.address)) {
        jsonPayload.data.address = allFormData.address || formData.address
      }

      _createBeneficiary(
        jsonPayload,
        (data) => {
          setLoading(false)
          setButtonLoading(false)
          dispatch(addBeneficiary(data))
          showSuccessSnackbar({
            alertMessage: "Beneficiary Successfully Created"
          })
          hideCreateBeneficiaryModal()
          if (modalData.callback) {
            modalData.callback()
          }
        },
        (error) => {
          console.log(error)
          setLoading(false)
          setButtonLoading(false)
          if (error) {
            showErrorSnackbar({
              alertMessage: error?.errors?.[0]?.length ? (
                <FormattedMessage id={error.errors} />
              ) : (
                "Unknown Error"
              )
            })
          }
        },
        false // isFormData flag - now using JSON
      )
    } catch (error) {
      console.error('Error processing files:', error)
      setLoading(false)
      setButtonLoading(false)
      showErrorSnackbar({
        alertMessage: "Error processing uploaded files"
      })
    }
  }

  // Reset modal state when opened/closed
  useEffect(() => {
    if (modalOpen) {
      // Set selected coin when modal opens
      if (modalData.selectedCoin) {
        // If a specific coin is provided, use it directly
        setSelectedBeneficiaryCoinID(modalData.selectedCoin)
      } else {
        // Only set default if no coin is specified
        if (currencies.length > 0) {
          const linkCurrency = currencies.find(c => c.id === "LINK")
          if (linkCurrency) {
            setSelectedBeneficiaryCoinID("LINK")
          } else {
            setSelectedBeneficiaryCoinID(currencies[0]?.id)
          }
        } else {
          // Fetch currencies and set default
          _getCurrencies((data) => {
            const linkCurrency = data.find(c => c.id === "LINK")
            if (linkCurrency) {
              setSelectedBeneficiaryCoinID("LINK")
            } else {
              setSelectedBeneficiaryCoinID(data[0]?.id)
            }
          })
        }
      }
    }

    if (!modalOpen) {
      // Reset all states
      setCurrentStep(1)
      setSelectedPaymentMethod(null)
      setPaymentMethodDetails(null)
      setPaymentMethods([])
      setNetworkOptions([])
      setSelectedNetwork("")
      setUploadedFiles({})
      setSelectedBeneficiaryCoinID(null) // Reset coin selection
      reset()
      setDisclaimerChecked(false)
    }
  }, [modalOpen, modalData.selectedCoin, currencies.length]) // Only depend on modalData.selectedCoin, not entire modalData

  // Fetch payment methods when modal opens and coin is set
  useEffect(() => {
    if (modalOpen && selectedBeneficiaryCoinID) {
      fetchPaymentMethods(selectedBeneficiaryCoinID)
    }
  }, [modalOpen, selectedBeneficiaryCoinID])

  // Handle network selection change
  const handleNetworkChange = (e, value) => {
    setSelectedNetwork(value)
    setValue("network", value)
    clearErrors("network")
  }

  // Handle country selection change
  const handleCountryChange = (countryCode) => {
    setValue(countryCode.target.name, countryCode.target.value)
    clearErrors(countryCode.target.name)
  }

  // Handle disclaimer checkbox change
  const handleDisclaimerChange = (e) => {
    setDisclaimerChecked(e.target.checked)
  }

  // Handle file upload change
  const handleFileChange = (fieldName, files) => {
    setUploadedFiles(prev => ({
      ...prev,
      [fieldName]: files
    }))

    // Clear any existing errors for this field
    clearErrors(fieldName)
  }

  // Handle back button
  const handleBackToStep1 = () => {
    // Get current form data
    const currentData = getValues()

    // Prepare step 1 data
    const step1Data = {
      name: currentData.name || "",
      description: currentData.description || "",
      payment_method: selectedPaymentMethod?.id?.toString() || ""
    }

    // Move back to step 1
    setCurrentStep(1)

    // Reset form with step 1 schema and data
    setTimeout(() => {
      reset(step1Data, {
        resolver: yupResolver(step1Schema),
        keepValues: false
      })
      setFormKey(prev => prev + 1)
    }, 50)
  }

  // Render payment method field options
  const renderPaymentMethodField = (field) => {
    const fieldName = field.field_name
    // Create a safe field name for form registration by removing special characters
    const safeFieldName = fieldName.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()
    const fieldLabel = field.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

    // Handle file upload fields
    if (field.upload) {
      return (
        <FileInput
          key={safeFieldName}
          label={fieldLabel}
          multiple={true}
          maxFiles={2} // Limit to 2 files as requested
          maxSize={5000000} // 5MB max file size
          accept={[".png", ".jpg", ".jpeg", ".pdf", ".docx"]}
          required={field.required}
          onFileChange={(files) => handleFileChange(safeFieldName, files)}
          error={errors[safeFieldName]}
          mainContainerStyle={{ marginBottom: "20px" }}
        />
      )
    }

    switch (fieldName.toLowerCase()) {
      case 'network':
        return (
          <Input
            key={safeFieldName}
            type="select"
            name={safeFieldName}
            inputLabel="Network"
            onChange={handleNetworkChange}
            selectValue={selectedNetwork}
            selectOptions={networkOptions}
            error={errors[safeFieldName]}
            errorMessage={errors[safeFieldName]?.message}
            isLoading={networkLoading}
            disabled={networkLoading}
            placeholder="Select network"
            inputIconContainerStyle={{
              border: "1px var(--border-color) solid",
              borderRadius: 5,
              background: "var(--secondary-color)",
              marginBottom: "20px"
            }}
            showBorder
            required
          />
        )

      case 'bank_country':
        return (
          <div key={safeFieldName}>
            <Input
              type="country"
              name={safeFieldName}
              inputLabel="Bank Country"
              placeholder="Select bank country"
              onChange={handleCountryChange}
              error={errors[safeFieldName]}
              errorMessage={errors[safeFieldName]?.message}
              inputIconContainerStyle={{
                border: "1px var(--border-color) solid",
                borderRadius: 5,
                background: "var(--secondary-color)",
                marginBottom: "20px"
              }}
              required
            />
            <input
              type="hidden"
              {...register(safeFieldName)}
            />
          </div>
        )

      default:
        return (
          <Input
            key={safeFieldName}
            inputLabel={fieldLabel}
            placeholder={`Enter ${fieldLabel.toLowerCase()}`}
            showBorder
            error={errors[safeFieldName]}
            errorMessage={errors[safeFieldName]?.message}
            required={field.required}
            {...register(safeFieldName)}
          />
        )
    }
  }

  return (
    <>
      <WenbitModal
        open={modalOpen}
        showCloseButton={true}
        onClose={hideCreateBeneficiaryModal}
        title="Manage Beneficiaries"
        render={() => (
          <div>
            {currentStep === 1 ? (
              // Step 1: Basic info and payment method selection
              <form key={formKey} onSubmit={handleSubmit(handleStep1Submit)} noValidate>
                <SelectCoinInput
                  onChange={(coin) => {
                    setSelectedBeneficiaryCoinID(coin?.id)
                  }}
                  coinId={selectedBeneficiaryCoinID}
                />

                <Input
                  inputLabel="Beneficiary Reference Name"
                  placeholder="Beneficiary reference name"
                  showBorder
                  error={errors.name}
                  errorMessage={errors.name?.message}
                  required
                  {...register("name")}
                />

                <Input
                  inputLabel="Beneficiary Description"
                  placeholder="Beneficiary description"
                  showBorder
                  error={errors.description}
                  errorMessage={errors.description?.message}
                  {...register("description")}
                />

                <div className={Styles.formSectionTitle}>
                  <h5>Choose Payment method:</h5>
                </div>

                {paymentMethodsLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    Loading payment methods...
                  </div>
                ) : (
                  <div className={Styles.paymentMethodsContainer}>
                    {paymentMethods.map((method) => (
                      <label
                        key={method.id}
                        className={Styles.paymentMethodOption}
                      >
                        <input
                          type="radio"
                          name="payment_method"
                          value={method.id}
                          checked={selectedPaymentMethod?.id === method.id}
                          onChange={(e) => handlePaymentMethodChange(e, method.id.toString())}
                          className={Styles.paymentMethodRadio}
                        />
                        <div className={Styles.paymentMethodName}>
                          {method.name}
                          {/* {method.fee_percentage && (
                            <div className={Styles.feePercentage}>
                              Fee: {method.fee_percentage}%
                            </div>
                          )} */}
                        </div>
                        <div className={Styles.paymentMethodIcon}>
                          {method.icon_url ? (
                            <img src={method.icon_url} alt={method.name} />
                          ) : (
                            <div className={Styles.defaultIcon}>
                              {method.name.charAt(0)}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                <input
                  type="hidden"
                  {...register("payment_method")}
                  value={selectedPaymentMethod?.id || ""}
                />

                {errors.payment_method && (
                  <div className={Styles.errorMessage}>
                    {errors.payment_method.message}
                  </div>
                )}

                <Button
                  text="Confirm"
                  disabled={loading || isSubmitting || paymentMethodsLoading || !selectedPaymentMethod}
                  isLoading={buttonLoading}
                  type="submit"
                  style={{ marginTop: "20px" }}
                />
              </form>
            ) : (
              // Step 2: Payment method specific fields
              <form key={formKey} onSubmit={handleSubmit(onFinalSubmit)} noValidate>
                {/* Show step 1 fields - still editable */}
                <SelectCoinInput
                  onChange={(coin) => {
                    setSelectedBeneficiaryCoinID(coin?.id)
                  }}
                  coinId={selectedBeneficiaryCoinID}
                />

                <Input
                  inputLabel="Beneficiary Reference Name"
                  placeholder="Beneficiary reference name"
                  showBorder
                  error={errors.name}
                  errorMessage={errors.name?.message}
                  required
                  {...register("name")}
                />

                <Input
                  inputLabel="Beneficiary Description"
                  placeholder="Beneficiary description"
                  showBorder
                  error={errors.description}
                  errorMessage={errors.description?.message}
                  {...register("description")}
                />

                <div className={Styles.formSectionTitle}>
                  <h5>Choose Payment method:</h5>
                </div>

                {paymentMethodsLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    Loading payment methods...
                  </div>
                ) : (
                  <div className={Styles.paymentMethodsContainer}>
                    {paymentMethods.map((method) => (
                      <label
                        key={method.id}
                        className={Styles.paymentMethodOption}
                      >
                        <input
                          type="radio"
                          name="payment_method"
                          value={method.id}
                          checked={selectedPaymentMethod?.id === method.id}
                          onChange={(e) => handlePaymentMethodChange(e, method.id.toString())}
                          className={Styles.paymentMethodRadio}
                        />
                        <div className={Styles.paymentMethodName}>
                          {method.name}
                          {/* {method.fee_percentage && (
                            <div className={Styles.feePercentage}>
                              Fee: {method.fee_percentage}%
                            </div>
                          )} */}
                        </div>
                        <div className={Styles.paymentMethodIcon}>
                          {method.icon_url ? (
                            <img src={method.icon_url} alt={method.name} />
                          ) : (
                            <div className={Styles.defaultIcon}>
                              {method.name.charAt(0)}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                <input
                  type="hidden"
                  {...register("payment_method")}
                  value={selectedPaymentMethod?.id || ""}
                />

                {paymentMethodDetailsLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    Loading payment method details...
                  </div>
                ) : (
                  <>
                    {/* For crypto currencies, always show address field */}
                    {!isFiatCurrency && (
                      <Input
                        inputLabel="Beneficiary Address"
                        placeholder="Enter beneficiary address"
                        showBorder
                        error={errors.address}
                        errorMessage={errors.address?.message}
                        required
                        {...register("address")}
                      />
                    )}

                    {/* For crypto currencies, show network field */}
                    {!isFiatCurrency && (
                      <Input
                        type="select"
                        name="network"
                        inputLabel="Network"
                        onChange={handleNetworkChange}
                        selectValue={selectedNetwork}
                        selectOptions={networkOptions}
                        error={errors.network}
                        errorMessage={errors.network?.message}
                        isLoading={networkLoading}
                        disabled={networkLoading}
                        placeholder="Select network"
                        inputIconContainerStyle={{
                          border: "1px var(--border-color) solid",
                          borderRadius: 5,
                          background: "var(--secondary-color)",
                          marginBottom: "20px"
                        }}
                        showBorder
                        required
                      />
                    )}

                    {/* Render additional dynamic fields from payment method details */}
                    {paymentMethodDetails?.payment_method_details
                      ?.filter(field => !['address', 'network'].includes(field.field_name.toLowerCase()))
                      ?.map(field => renderPaymentMethodField(field))}

                    {/* Hidden input for network */}
                    <input type="hidden" {...register("network")} value={selectedNetwork} />

                    {/* Disclaimer checkbox */}
                    <div className={Styles.disclaimerContainer}>
                      <div className={Styles.checkbox}>
                        <input
                          type="checkbox"
                          id="disclaimer-checkbox"
                          checked={disclaimerChecked}
                          onChange={handleDisclaimerChange}
                        />
                        <label htmlFor="disclaimer-checkbox">
                          <span className={Styles.disclaimerText}>
                            Disclaimer: By creating a beneficiary, you confirm that all wallet addresses / accounts and beneficiary details provided are accurate and belong to the intended recipient. You acknowledge that it is your sole responsibility to verify and ensure the correctness of this information. The company shall not be held liable for any loss, delay, or misdirection of funds resulting from incorrect, incomplete, or fraudulent beneficiary details.
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className={Styles.buttonContainer}>
                      <Button
                        text="Back"
                        onClick={handleBackToStep1}
                        style={{ marginTop: "20px", marginRight: "10px" }}
                        variant="outlined"
                      />
                      <Button
                        text="Confirm"
                        disabled={loading || isSubmitting || paymentMethodDetailsLoading || !disclaimerChecked}
                        isLoading={buttonLoading}
                        type="submit"
                        style={{ marginTop: "20px" }}
                      />
                    </div>
                  </>
                )}
              </form>
            )}
          </div>
        )}
      />
    </>
  )
}

export default CreateBeneficiaryModal